import { z } from 'zod';
import { CONTENT_PILLAR_LIMITS, KNOWLEDGE_CATEGORIES, KNOWLEDGE_ITEM_LIMITS, PROPERTY_ADDRESS_LIMITS } from '../knowledge.js';
import { WRITING_ADDRESS_FORMS, WRITING_EMOJI_USES, WRITING_NARRATIVE_VOICES } from '../writing-style.js';
/**
 * `website_reader` (spec 20 section 5.3, spec 80 section 4.13, S160-7,
 * REQ-AUT-016/017): reads the public pages of a business's own website and
 * proposes master data, knowledge items with their source page, tone, content
 * pillars and photo candidates. Knowledge becomes `draft` items (never used by
 * AI before a human verifies them); everything else stays a proposal in the
 * run output that a person applies, and photos are imported only after the
 * business confirms it may use them.
 */
export const WEBSITE_READER_LIMITS = {
    urlMaxLength: 500,
    /** Pages read per run, all on the same host (REQ-AUT-016). */
    maxPages: 30,
    /** Bytes read of one page; the rest is ignored. */
    maxPageBytes: 1_000_000,
    /** Text kept per page and in total, so the context stays within one prompt. */
    maxPageChars: 6_000,
    maxTotalChars: 60_000,
    /** Image candidates the context lists, in page order (declared icons and logos left out). */
    maxImageCandidates: 40,
    maxKnowledge: 25,
    maxPillars: 6,
    maxPhotos: 12,
    descriptionMaxLength: 5000,
    locationMaxLength: 500,
    regionMaxLength: 120,
    brandToneMaxLength: 2000,
    phoneMaxLength: 40,
    emailMaxLength: 254,
    altMaxLength: 300,
};
/** A website address as the import takes it: absolute `http(s)` URL without credentials. */
export function isWebsiteUrl(value) {
    if (value.length > WEBSITE_READER_LIMITS.urlMaxLength)
        return false;
    let url;
    try {
        url = new URL(value);
    }
    catch {
        return false;
    }
    return (url.protocol === 'https:' || url.protocol === 'http:') && url.username === '' && url.password === '' && url.hostname.includes('.');
}
/** Sections a website run can be limited to (E210, spec 80 section 4.17). */
export const WEBSITE_READER_SECTIONS = ['property', 'brand', 'knowledge', 'pillars', 'style'];
export const websiteReaderInputSchema = z.object({
    property_id: z.uuid(),
    url: z.string().refine(isWebsiteUrl, { message: 'expected an http(s) website address' }),
    /** Absent = the setup run with its effect; present = a section run without effect. */
    sections: z
        .array(z.enum(WEBSITE_READER_SECTIONS))
        .min(1)
        .refine((sections) => new Set(sections).size === sections.length, { message: 'sections must be unique' })
        .optional(),
});
const text = (max) => z.string().trim().max(max);
/** A postal address as the model proposes it; the country is checked as ISO alpha-2 by the job (E210, REQ-ADR-008). */
export const websiteAddressSchema = z.object({
    street: text(PROPERTY_ADDRESS_LIMITS.streetMaxLength).nullable(),
    postal_code: text(PROPERTY_ADDRESS_LIMITS.postalCodeMaxLength).nullable(),
    city: text(PROPERTY_ADDRESS_LIMITS.cityMaxLength).nullable(),
    country: text(2).nullable(),
});
/** The five brand colour slots of module 90 (spec 90 section 4.1) a brand run can propose. */
export const WEBSITE_BRAND_SLOTS = ['primary', 'secondary', 'accent', 'background', 'text'];
/** Most logo candidates a run keeps (spec 80 section 4.17). */
export const WEBSITE_LOGO_CANDIDATE_LIMIT = 3;
/** One proposed colour: a candidate of the context (`ref`, with its own `hex`) and why, in the website's language. */
export const websiteBrandColorSchema = z.object({
    hex: z.string().regex(/^#[0-9A-F]{6}$/),
    ref: z.string().max(8),
    reason: text(120),
});
/** Only the colour slots come from the model; logo and fonts are computed in code and added to the stored output. */
export const websiteBrandOutputSchema = z.object({
    colors: z.object({
        primary: websiteBrandColorSchema.nullable(),
        secondary: websiteBrandColorSchema.nullable(),
        accent: websiteBrandColorSchema.nullable(),
        background: websiteBrandColorSchema.nullable(),
        text: websiteBrandColorSchema.nullable(),
    }),
});
export const EMPTY_WEBSITE_BRAND = { colors: { primary: null, secondary: null, accent: null, background: null, text: null } };
export const websiteReaderOutputSchema = z.object({
    property: z.object({
        description: text(WEBSITE_READER_LIMITS.descriptionMaxLength).nullable(),
        location: text(WEBSITE_READER_LIMITS.locationMaxLength).nullable(),
        region: text(WEBSITE_READER_LIMITS.regionMaxLength).nullable(),
        contact_email: text(WEBSITE_READER_LIMITS.emailMaxLength).nullable(),
        contact_phone: text(WEBSITE_READER_LIMITS.phoneMaxLength).nullable(),
        brand_tone: text(WEBSITE_READER_LIMITS.brandToneMaxLength).nullable(),
        /** Prompt v2; a v1 answer has none and parses as `null`. */
        address: websiteAddressSchema.nullable().default(null),
    }),
    knowledge: z
        .array(z.object({
        category: z.enum(KNOWLEDGE_CATEGORIES),
        title: text(KNOWLEDGE_ITEM_LIMITS.titleMaxLength).min(1),
        body: text(KNOWLEDGE_ITEM_LIMITS.bodyMaxLength).min(1),
        source_url: z.string().max(KNOWLEDGE_ITEM_LIMITS.sourceUrlMaxLength),
    }))
        .max(WEBSITE_READER_LIMITS.maxKnowledge),
    writing_style: z.object({
        address_form: z.enum(WRITING_ADDRESS_FORMS).nullable(),
        emoji_use: z.enum(WRITING_EMOJI_USES).nullable(),
        narrative_voice: z.enum(WRITING_NARRATIVE_VOICES).nullable(),
    }),
    pillars: z
        .array(z.object({ name: text(CONTENT_PILLAR_LIMITS.nameMaxLength).min(1), description: text(CONTENT_PILLAR_LIMITS.descriptionMaxLength) }))
        .max(WEBSITE_READER_LIMITS.maxPillars),
    photos: z
        .array(z.object({ url: z.string().max(KNOWLEDGE_ITEM_LIMITS.sourceUrlMaxLength), page_url: z.string().max(KNOWLEDGE_ITEM_LIMITS.sourceUrlMaxLength), alt: text(WEBSITE_READER_LIMITS.altMaxLength) }))
        .max(WEBSITE_READER_LIMITS.maxPhotos),
    /** Prompt v2, `brand` section; a v1 answer has none and parses as all `null`. */
    brand: websiteBrandOutputSchema.default(EMPTY_WEBSITE_BRAND),
});
/**
 * What the model is asked for (prompt v3): the same answer, but a value the pages do not state is an
 * empty string instead of `null`. Anthropic accepts at most 16 union-typed (nullable) parameters in a
 * strict schema, and the nullable-per-field shape had 19. Eleven of them are scalar strings and the
 * address, which are flattened here; `normalizeWebsiteReaderAnswer` turns the answer back into
 * `websiteReaderOutputSchema`'s shape before it is validated and stored.
 */
export const websiteReaderModelSchema = z.object({
    property: z.object({
        description: text(WEBSITE_READER_LIMITS.descriptionMaxLength),
        location: text(WEBSITE_READER_LIMITS.locationMaxLength),
        region: text(WEBSITE_READER_LIMITS.regionMaxLength),
        contact_email: text(WEBSITE_READER_LIMITS.emailMaxLength),
        contact_phone: text(WEBSITE_READER_LIMITS.phoneMaxLength),
        brand_tone: text(WEBSITE_READER_LIMITS.brandToneMaxLength),
        address: z.object({
            street: text(PROPERTY_ADDRESS_LIMITS.streetMaxLength),
            postal_code: text(PROPERTY_ADDRESS_LIMITS.postalCodeMaxLength),
            city: text(PROPERTY_ADDRESS_LIMITS.cityMaxLength),
            country: text(2),
        }),
    }),
    knowledge: websiteReaderOutputSchema.shape.knowledge,
    writing_style: websiteReaderOutputSchema.shape.writing_style,
    pillars: websiteReaderOutputSchema.shape.pillars,
    photos: websiteReaderOutputSchema.shape.photos,
    brand: websiteBrandOutputSchema,
});
const blankToNull = (value) => (typeof value === 'string' ? value.trim() || null : value ?? null);
/**
 * The model's answer as `websiteReaderOutputSchema` parses it: an empty string (or `null`, or a missing
 * value, as prompt v1 and v2 answers have it) becomes `null`, and an address with no part stated becomes
 * `null`. Anything that is not an object is returned unchanged for the schema to reject.
 */
export function normalizeWebsiteReaderAnswer(raw) {
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw))
        return raw;
    const answer = raw;
    const property = answer['property'];
    if (property === null || typeof property !== 'object' || Array.isArray(property))
        return raw;
    const fields = Object.fromEntries(Object.entries(property).map(([key, value]) => [key, key === 'address' ? value : blankToNull(value)]));
    const address = fields['address'];
    if (address === null || typeof address !== 'object' || Array.isArray(address)) {
        fields['address'] = null;
    }
    else {
        const parts = Object.fromEntries(['street', 'postal_code', 'city', 'country'].map((key) => [key, blankToNull(address[key])]));
        fields['address'] = Object.values(parts).every((value) => value === null) ? null : parts;
    }
    return { ...answer, property: fields };
}
/** The sections of a run: the given ones, else the setup set (`brand` is not part of it). */
export function websiteReaderSections(sections) {
    return new Set(sections ?? ['property', 'knowledge', 'pillars', 'style']);
}
//# sourceMappingURL=website-reader.js.map